import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Card,
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Select,
  message,
  Popconfirm,
  Tag,
  Row,
  Col,
  DatePicker,
  Upload,
  Dropdown,
  Progress,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  ExportOutlined,
  ImportOutlined,
  DownloadOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import followUpService, { FollowUp, FollowUpListParams, FilterOption } from '../services/followUp';
import { optionService } from '../services/option';
import { NATION_OPTIONS } from '../config/nations';
import { SERVICE_TYPE_OPTIONS } from '../config/service-types';
import { SERVICE_TYPE_MAP } from '../config/service-type-map';
import ColumnSelector from '../components/ColumnSelector';
import userPreferenceService from '../services/userPreference';
import usePermissions from '../hooks/usePermissions';
import useDocumentTitle from '../hooks/useDocumentTitle';
import FollowUpRecordModal from '../components/follow-up/FollowUpRecordModal';
import { INTENT_OPTIONS, STATUS_OPTIONS, CLIENT_TYPE_OPTIONS } from '../config/follow-up-options';
import { filterActionColumnByAccess, hasAnyRowActionAccess } from '../utils/action-permissions';
import ListHelpButton from '../components/ListHelpButton';

const { Option } = Select;
const { TextArea } = Input;

const FollowUpManagement: React.FC = () => {
  useDocumentTitle('客户跟进');
  const permissions = usePermissions();
  const [urlSearchParams, setUrlSearchParams] = useSearchParams();
  const [followUps, setFollowUps] = useState<FollowUp[]>([]);
  const [loading, setLoading] = useState(false);

  // 从 URL 参数初始化分页状态
  const [pagination, setPagination] = useState({
    current: parseInt(urlSearchParams.get('page') || '1', 10),
    pageSize: parseInt(urlSearchParams.get('pageSize') || '10', 10),
    total: 0,
  });

  // 用于跟踪是否已经初始化
  const isInitialized = useRef(false);
  const [searchParams, setSearchParams] = useState<FollowUpListParams>({});
  const [modalVisible, setModalVisible] = useState(false);
  const [editingFollowUp, setEditingFollowUp] = useState<FollowUp | null>(null);
  const [form] = Form.useForm();
  const [searchText, setSearchText] = useState('');

  // 导入导出相关状态
  const [importModalVisible, setImportModalVisible] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [visibleColumns, setVisibleColumns] = useState<string[]>([]);
  const navigate = useNavigate();
  // 权限与批量操作
  const canCreate = permissions.hasActionAccess('follow-up-management', 'create');
  const canEdit = permissions.hasActionAccess('follow-up-management', 'edit');
  const canImport = permissions.hasActionAccess('follow-up-management', 'import');
  const canExport = permissions.hasActionAccess('follow-up-management', 'export');
  const canDelete = permissions.hasActionAccess('follow-up-management', 'delete');
  const canViewDetail = permissions.hasActionAccess('follow-up-management', 'view_details');
  const hasRowActionAccess = hasAnyRowActionAccess(permissions, 'follow-up-management', ['view_details', 'edit', 'delete']);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const rowSelection = React.useMemo(() => ({
    selectedRowKeys,
    onChange: (keys: React.Key[]) => setSelectedRowKeys(keys),
    preserveSelectedRowKeys: true,
  }), [selectedRowKeys]);

  // 筛选状态
  const [filtersState, setFilters] = useState({
    nation: '',
    customer_categories: '',
    source: '',
    service: '',
    intent: '',
    status: '',
    user_id: '',
  });

  // 使用 useMemo 稳定 filters 对象引用
  const filters = useMemo(() => filtersState, [
    filtersState.nation,
    filtersState.customer_categories,
    filtersState.source,
    filtersState.service,
    filtersState.intent,
    filtersState.status,
    filtersState.user_id,
  ]);

  // 动态筛选选项
  const [filterOptions, setFilterOptions] = useState({
    nations: [] as FilterOption[],
    customer_categories: [] as FilterOption[],
    sources: [] as FilterOption[],
    services: [] as FilterOption[],
    intents: [] as FilterOption[],
    statuses: [] as FilterOption[],
    sellers: [] as { value: number; label: string }[],
  });

  // 客户类型选项
  const clientTypeOptions = CLIENT_TYPE_OPTIONS;

  // 客户来源选项（与客户信息表单保持一致，来自后台设置）
  const [sourceOptions, setSourceOptions] = useState<Array<{ value: string; label: string }>>([]);

  // 意向程度选项
  const intentOptions = INTENT_OPTIONS;

  // 跟进状态选项
  const statusOptions = STATUS_OPTIONS;

  // 国家选项
  const nationOptions = NATION_OPTIONS;

  // 获取筛选选项
  const fetchFilterOptions = async () => {
    try {
      const response = await followUpService.getFilterOptions();
      if (response.success) {
        setFilterOptions(response.data);
      }
    } catch (error) {
      console.error('获取筛选选项失败:', error);
    }
  };

  // 处理筛选变化
  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // 重置筛选
  const handleResetFilters = () => {
    setFilters({
      nation: '',
      customer_categories: '',
      source: '',
      service: '',
      intent: '',
      status: '',
      user_id: '',
    });
    setSearchText('');
  };

  // 业务类型选项
  const serviceTypeOptions = SERVICE_TYPE_OPTIONS;

  const getServiceNames = (serviceType: string) => {
    if (!serviceType) return [] as string[];
    const map = (SERVICE_TYPE_MAP as Record<string, Record<string, string>>)[serviceType] || {};
    return Object.values(map);
  };

  // 监控表单中的业务类型变化
  const [selectedServiceType, setSelectedServiceType] = useState<string>(SERVICE_TYPE_OPTIONS[0]?.value || '');

  // 业务名称选项 - 根据 service-type-map 动态获取
  const serviceNameOptions = useMemo(() => {
    return getServiceNames(selectedServiceType);
  }, [selectedServiceType]);
  const [recordModalVisible, setRecordModalVisible] = useState(false);
  const [recordFollowUpId, setRecordFollowUpId] = useState<number | undefined>(undefined);

  // 加载客户来源配置
  useEffect(() => {
    const loadSourceOptions = async () => {
      try {
        const customerSources = await optionService.get('customer_sources', []);
        if (Array.isArray(customerSources)) {
          setSourceOptions(customerSources.map((source: string) => ({ value: source, label: source })));
        }
      } catch (error) {
        console.error('加载客户来源失败:', error);
        setSourceOptions([]);
      }
    };
    loadSourceOptions();
  }, []);

  // 获取跟进数据
  const fetchFollowUps = async (params?: FollowUpListParams) => {
    try {
      setLoading(true);

      // 构建请求参数
      const requestParams: any = {
        page: pagination.current,
        per_page: pagination.pageSize,
        search: searchText,
        ...searchParams,
        ...params,
      };

      // 添加筛选参数
      Object.keys(filters).forEach(key => {
        if (filters[key as keyof typeof filters]) {
          requestParams[key] = filters[key as keyof typeof filters];
        }
      });

      const response = await followUpService.getFollowUps(requestParams);

      if (response.success) {
        setFollowUps(response.data.data);
        // 只更新总数，不更新分页状态
        setPagination(prev => ({
          ...prev,
          total: response.data.total,
        }));
      }
    } catch (error) {
      console.error('获取跟进数据失败:', error);
      message.error('获取跟进数据失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFilterOptions();
    loadPageSizeSettings();
    // 延迟标记初始化完成，确保分页状态已设置
    setTimeout(() => {
      isInitialized.current = true;
      // 初始化完成后获取第一次数据
      fetchFollowUps();
    }, 0);
  }, []);

  // 搜索、筛选、排序变化时重置到第一页（排序除外）
  useEffect(() => {
    // 跳过初始化时的触发
    if (!isInitialized.current) return;

    if (pagination.current !== 1) {
      const newPagination = { ...pagination, current: 1 };
      setPagination(newPagination);
      updateUrlParams(newPagination);
    } else {
      fetchFollowUps();
    }
  }, [searchText, filters]);

  // 排序变化时获取数据（不重置页码）
  useEffect(() => {
    // 跳过初始化时的触发
    if (!isInitialized.current) return;

    fetchFollowUps();
  }, [searchParams]);

  // 分页变化时获取数据
  useEffect(() => {
    // 跳过初始化时的触发
    if (!isInitialized.current) return;

    if (pagination.current > 0 && pagination.pageSize > 0) {
      fetchFollowUps();
    }
  }, [pagination.current, pagination.pageSize]);

  // 同步分页状态到 URL
  const updateUrlParams = (newPagination: { current: number; pageSize: number; total?: number }) => {
    const newSearchParams = new URLSearchParams(urlSearchParams);
    newSearchParams.set('page', newPagination.current.toString());
    newSearchParams.set('pageSize', newPagination.pageSize.toString());
    setUrlSearchParams(newSearchParams, { replace: true });
  };

  // 加载每页条数设置
  const loadPageSizeSettings = async () => {
    try {
      const savedPageSize = await userPreferenceService.getPageSizeSettings('followUps');
      // 优先使用 URL 参数，如果没有则使用保存的偏好
      const urlPageSize = parseInt(urlSearchParams.get('pageSize') || '0', 10);
      const finalPageSize = urlPageSize > 0 ? urlPageSize : savedPageSize;

      // 只更新 pageSize，不更新 current，避免覆盖 URL 中的页码
      if (pagination.pageSize !== finalPageSize) {
        setPagination(prev => ({
          ...prev,
          pageSize: finalPageSize
        }));
        // 同步 pageSize 到 URL（保持当前页码不变）
        const newSearchParams = new URLSearchParams(urlSearchParams);
        newSearchParams.set('pageSize', finalPageSize.toString());
        setUrlSearchParams(newSearchParams, { replace: true });
      }
    } catch (error) {
      console.error('加载每页条数设置失败:', error);
    }
  };

  // 搜索处理
  const handleSearch = () => {
    const params = {
      search: searchText,
    };
    setSearchParams(params);
    setPagination({ ...pagination, current: 1 });
    fetchFollowUps({ ...params, page: 1 });
  };

  // 表格分页和排序变化
  const handleTableChange = async (paginationConfig: any, filters: any, sorter: any) => {
    const isPageSizeChanged = pagination.pageSize !== paginationConfig.pageSize;

    // 处理排序变化
    let updatedSearchParams = { ...searchParams };
    if (sorter && sorter.field && sorter.order) {
      updatedSearchParams.sort_by = sorter.field;
      updatedSearchParams.sort_direction = sorter.order === 'ascend' ? 'asc' : 'desc';
    } else if (sorter && !sorter.order) {
      // 清除排序
      delete updatedSearchParams.sort_by;
      delete updatedSearchParams.sort_direction;
    }
    setSearchParams(updatedSearchParams);

    // 如果每页条数发生变化，保存用户偏好并从第一页开始
    if (isPageSizeChanged) {
      try {
        await userPreferenceService.savePageSizeSettings('followUps', paginationConfig.pageSize);
      } catch (error) {
        console.error('保存每页条数设置失败:', error);
      }

      const newPagination = {
        ...pagination,
        current: 1, // 从第一页开始
        pageSize: paginationConfig.pageSize,
      };
      setPagination(newPagination);
      updateUrlParams(newPagination);
    } else {
      // 只是翻页或排序，更新页码
      const newPagination = {
        ...pagination,
        current: paginationConfig.current,
        pageSize: paginationConfig.pageSize,
      };
      setPagination(newPagination);
      updateUrlParams(newPagination);
    }
  };

  // 所有可用的列定义
  const allColumnDefinitions: Record<string, any> = {
    contact_name: {
      title: '客户姓名',
      dataIndex: 'contact_name',
      key: 'contact_name',
      width: 120,
      fixed: 'left',
    },
    contact_info: {
      title: '联系方式',
      dataIndex: 'contact_info',
      key: 'contact_info',
      width: 120,
    },
    sub_service: {
      title: '子业务名称',
      dataIndex: 'sub_service',
      key: 'sub_service',
      width: 120,
    },
    service: {
      title: '业务类型',
      dataIndex: 'service',
      key: 'service',
      width: 100,
      render: (service: string) => {
        const option = serviceTypeOptions.find(opt => opt.value === service);
        return <Tag color="blue">{option?.label || service}</Tag>;
      },
    },
    nation: {
      title: '国家',
      dataIndex: 'nation',
      key: 'nation',
      width: 80,
      render: (nation: string) => {
        const option = nationOptions.find(opt => opt.value === nation);
        return option?.label || nation;
      },
    },
    customer_categories: {
      title: '客户类型',
      dataIndex: 'customer_categories',
      key: 'customer_categories',
      width: 100,
      sorter: true,
      sortDirections: ['ascend', 'descend'],
    },
    source: {
      title: '客户来源',
      dataIndex: 'source',
      key: 'source',
      width: 100,
      sorter: true,
      sortDirections: ['ascend', 'descend'],
    },
    intent: {
      title: '意向程度',
      dataIndex: 'intent',
      key: 'intent',
      width: 100,
      sorter: true,
      sortDirections: ['ascend', 'descend'],
      render: (intent: string) => {
        const option = intentOptions.find(opt => opt.value === intent);
        return <Tag color={option?.color}>{option?.label || intent}</Tag>;
      },
    },
    status: {
      title: '跟进状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      sorter: true,
      sortDirections: ['ascend', 'descend'],
      render: (status: string) => {
        const option = statusOptions.find(opt => opt.value === status);
        return <Tag color={option?.color}>{option?.label || status}</Tag>;
      },
    },
    created_at: {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 120,
      sorter: true,
      sortDirections: ['ascend', 'descend'],
      render: (date: string) => date ? dayjs(date).format('YYYY-MM-DD') : '-',
    },
    updated_at: {
      title: '最后更新',
      dataIndex: 'updated_at',
      key: 'updated_at',
      width: 120,
      render: (date: string) => date ? dayjs(date).format('YYYY-MM-DD') : '-',
    },
    // remark: {
    //   title: '备注',
    //   dataIndex: 'remark',
    //   key: 'remark',
    //   width: 160,
    //   ellipsis: false,
    //   render: (remark: string) => remark || '-',
    // },
    latest_record: {
      title: '最新跟进记录',
      dataIndex: 'latest_record',
      key: 'latest_record',
      width: 160,
      ellipsis: false,
      render: (latest_record: any) => latest_record?.remark || '-',
    },
    user: {
      title: '所属销售',
      dataIndex: 'user',
      key: 'user',
      width: 100,
      render: (user: { id: number; display_name: string } | undefined) => user?.display_name || '-',
    },
    actions: {
      title: '操作',
      key: 'actions',
      fixed: 'right',
      width: 140,
      render: (_: any, record: FollowUp) => {
        const actions = [];

        // 查看按钮只在拥有详情权限时显示，避免入口与接口权限不一致。
        if (canViewDetail) {
          actions.push(
            <Button
              key="view"
              icon={<EyeOutlined />}
              onClick={() => navigate(`/follow-ups/${record.id}`)}
              size="small"
              title="查看"
            />
          );
        }

        // 编辑按钮 - 根据权限控制
        if (canEdit) {
          actions.push(
            <Button
              key="edit"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
              size="small"
              title="编辑"
            />
          );
        }

        // 更新记录
        if (canEdit) {
          actions.push(
            <Button
              key="record"
              icon={<PlusOutlined />}
              onClick={() => {
                setRecordFollowUpId(record.id);
                setRecordModalVisible(true);
              }}
              size="small"
              title="更新记录"
            />
          );
        }

        // 删除按钮 - 根据权限控制
        if (canDelete) {
          actions.push(
            <Popconfirm
              key="delete"
              title="确定删除这条客户跟进吗？"
              onConfirm={() => record.id && handleDelete(record.id)}
              okText="确定"
              cancelText="取消"
            >
              <Button
                danger
                icon={<DeleteOutlined />}
                size="small"
                title="删除"
              />
            </Popconfirm>
          );
        }

        return <Space>{actions}</Space>;
      },
    },
  };

  // 默认显示的列
  const defaultVisibleColumns = [
    'contact_name',
    'contact_info',
    'sub_service',
    'service',
    'nation',
    'customer_categories',
    'source',
    'intent',
    'status',
    'user',
    'created_at',
    'updated_at',
    'remark',
    'latest_record',
    'actions'
  ];

  // 初始化可见列
  useEffect(() => {
    const loadColumnSettings = async () => {
      try {
        const savedColumns = await userPreferenceService.getColumnSettings('followUps');
        if (savedColumns && savedColumns.length > 0) {
          setVisibleColumns(savedColumns);
        } else {
          setVisibleColumns(defaultVisibleColumns);
        }
      } catch (error) {
        console.warn('加载列设置失败:', error);
        setVisibleColumns(defaultVisibleColumns);
      }
    };

    loadColumnSettings();
  }, []);

  // 生成表格列，根据权限过滤
  const columns: ColumnsType<FollowUp> = permissions.getVisibleColumns('follow-up-management',
    filterActionColumnByAccess(visibleColumns.map(columnKey => ({
      key: columnKey,
      ...allColumnDefinitions[columnKey]
    })), hasRowActionAccess)
  ).map(column => allColumnDefinitions[column.key]).filter(Boolean);

  // 获取可用列配置，根据权限过滤
  const availableColumns = permissions.getVisibleColumns('follow-up-management',
    filterActionColumnByAccess(Object.keys(allColumnDefinitions).map(key => ({
      key,
      title: allColumnDefinitions[key].title,
      fixed: allColumnDefinitions[key].fixed
    })), hasRowActionAccess)
  );

  // 处理列显示变更
  const handleColumnChange = async (checkedColumns: string[]) => {
    // 保留用户拖拽设置的顺序，只过滤掉 actions（固定在末尾）
    const filteredColumns = checkedColumns.filter(col => col !== 'actions');
    const uniqueColumns = [...filteredColumns, 'actions'];

    setVisibleColumns(uniqueColumns);

    try {
      await userPreferenceService.saveColumnSettings('followUps', uniqueColumns);
    } catch (error) {
      console.error('保存列设置失败:', error);
      message.error('保存列设置失败');
    }
  };

  // 处理新增/编辑
  const handleEdit = (followUp?: FollowUp) => {
    setEditingFollowUp(followUp || null);
    if (followUp) {
      // 处理日期字段，确保DatePicker可以正确识别
      const formData = {
        ...followUp,
        created_at: followUp.created_at ? dayjs(followUp.created_at) : null,
      };
      form.setFieldsValue(formData);
      setSelectedServiceType(followUp.service || '');
    } else {
      form.resetFields();
      const defaultService = SERVICE_TYPE_OPTIONS[0]?.value || '';
      setSelectedServiceType(defaultService);
      const names = getServiceNames(defaultService);
      form.setFieldsValue({ sub_service: names[0] });
    }
    setModalVisible(true);
  };

  // 处理业务类型变化
  const handleServiceTypeChange = (value: string) => {
    setSelectedServiceType(value);
    const names = getServiceNames(value);
    form.setFieldsValue({ sub_service: names[0] });
  };

  // 处理保存
  const handleSave = async () => {
    try {
      const values = await form.validateFields();

      // 处理日期字段，转换为后端接受的格式
      const submitData = {
        ...values,
        created_at: values.created_at ? values.created_at.format('YYYY-MM-DD') : null,
      };

      if (editingFollowUp) {
        await followUpService.updateFollowUp(editingFollowUp.id!, submitData);
        message.success('客户跟进更新成功');
      } else {
        await followUpService.createFollowUp(submitData);
        message.success('客户跟进添加成功');
      }

      setModalVisible(false);
      setEditingFollowUp(null);
      form.resetFields();
      const defaultService = SERVICE_TYPE_OPTIONS[0]?.value || '';
      setSelectedServiceType(defaultService);
      const names = getServiceNames(defaultService);
      form.setFieldsValue({ sub_service: names[0] });
      fetchFollowUps();
    } catch (error) {
      console.error('保存失败:', error);
      message.error('保存失败');
    }
  };

  // 处理删除
  const handleDelete = async (id: number) => {
    if (!id) {
      message.error('客户跟进ID无效');
      return;
    }
    try {
      await followUpService.deleteFollowUp(id);
      message.success('客户跟进删除成功');
      fetchFollowUps();
    } catch (error) {
      console.error('删除失败:', error);
      message.error('删除失败');
    }
  };

  // 批量删除
  const handleBulkDelete = async () => {
    if (!selectedRowKeys.length) return;
    try {
      const ids = selectedRowKeys as number[];
      const response = await followUpService.batchDeleteFollowUps(ids);
      if (response.success) {
        message.success(response.message || `成功删除 ${response.data?.deleted_count || ids.length} 条跟进记录`);
        setSelectedRowKeys([]);
        fetchFollowUps();
      } else {
        message.error(response.message || '批量删除失败');
      }
    } catch (e: any) {
      message.error(e?.response?.data?.message || '批量删除失败');
    }
  };

  // 处理导出
  const handleExport = async () => {
    try {
      const blob = await followUpService.exportFollowUps(searchParams);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `跟进记录数据_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      message.success('导出成功');
    } catch (error) {
      console.error('导出失败:', error);
      message.error('导出失败');
    }
  };

  // 处理导入
  const handleImport = async (file: any) => {
    setImportLoading(true);
    setImportProgress(0);

    try {
      const result = await followUpService.importFollowUps(file);

      if (result.success) {
        const { total, success, updated, failed, errors } = result.data;
        let messageText = `导入完成！总计: ${total}, 成功: ${success}, 更新: ${updated}`;

        if (failed > 0) {
          messageText += `, 失败: ${failed}`;
          if (errors && errors.length > 0) {
            messageText += `\n错误详情: ${errors.slice(0, 3).join('; ')}`;
            if (errors.length > 3) {
              messageText += '...';
            }
          }
        }

        if (failed === 0) {
          message.success(messageText);
        } else {
          message.warning(messageText);
        }

        fetchFollowUps(); // 刷新数据
      } else {
        message.error(`导入失败: ${result.message}`);
      }
    } catch (error) {
      console.error('导入失败:', error);
      message.error('导入失败，请检查文件格式');
    } finally {
      setImportLoading(false);
      setImportProgress(0);
      setImportModalVisible(false);
    }
  };

  // 检查页面访问权限
  if (!permissions.hasPageAccess('follow-up-management')) {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        <h3>无权限访问客户跟进页面</h3>
        <p>请联系管理员获取相应权限</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      <Card
        title={(
          <Space size="small">
            <span>客户跟进</span>
            <ListHelpButton page_key="follow_up_management" />
          </Space>
        )}
        extra={
          <Space>
            <ColumnSelector
              availableColumns={availableColumns}
              visibleColumns={visibleColumns}
              onColumnChange={handleColumnChange}
              fixedColumns={['contact_name', 'actions']}
            />
            {canCreate && (
              <Button type="primary" icon={<PlusOutlined />} onClick={() => handleEdit()} data-testid="btn-fup-add">
                新增跟进
              </Button>
            )}
            {(() => {
              const items: any[] = [];
              if (canExport) items.push({ key: 'export', label: '导出数据', icon: <ExportOutlined />, onClick: handleExport });
              if (canImport) items.push({ key: 'import', label: '导入数据', icon: <ImportOutlined />, onClick: () => setImportModalVisible(true) });
              return items.length > 0 ? (
                <Dropdown menu={{ items }}>
                  <Button data-testid="btn-fup-data-menu">数据管理</Button>
                </Dropdown>
              ) : null;
            })()}
            {canDelete && selectedRowKeys.length > 0&& (
              <Popconfirm
                title={`确定删除选中的 ${selectedRowKeys.length} 条记录吗？`}
                okText="确定"
                cancelText="取消"
                onConfirm={handleBulkDelete}
                disabled={!selectedRowKeys.length}
              >
                <Button danger disabled={!selectedRowKeys.length}>批量删除</Button>
              </Popconfirm>
            )}
          </Space>
        }
      >
        {/* 搜索和操作区域 */}
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col span={4}>
            <Input.Search
              placeholder="搜索客户姓名、联系方式、业务名称"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onSearch={handleSearch}
              enterButton
            />
          </Col>

          <Col span={20}>
            {/* 筛选区域 */}
            <Row gutter={[12, 16]} style={{ marginBottom: 16 }}>
              <Col span={3}>
                <Select
                  placeholder="国家"
                  style={{ width: '100%' }}
                  value={filters.nation || undefined}
                  onChange={(value) => handleFilterChange('nation', value || '')}
                  allowClear
                  showSearch
                  data-testid="fup-filter-nation"
                >
                  {(filterOptions?.nations || []).map(option => (
                    <Select.Option key={option.value} value={option.value}>
                      {option.label}
                    </Select.Option>
                  ))}
                </Select>
              </Col>
              <Col span={2.8}>
                <Select
                  placeholder="客户类型"
                  style={{ width: '100%' }}
                  value={filters.customer_categories || undefined}
                  onChange={(value) => handleFilterChange('customer_categories', value || '')}
                  allowClear
                  showSearch
                  data-testid="fup-filter-customer-categories"
                >
                  {(filterOptions?.customer_categories || []).map(option => (
                    <Select.Option key={option.value} value={option.value}>
                      {option.label}
                    </Select.Option>
                  ))}
                </Select>
              </Col>
              <Col span={4}>
                <Select
                  placeholder="客户来源"
                  style={{ width: '100%' }}
                  value={filters.source || undefined}
                  onChange={(value) => handleFilterChange('source', value || '')}
                  allowClear
                  showSearch
                  data-testid="fup-filter-source"
                >
                  {(filterOptions?.sources || []).map(option => (
                    <Select.Option key={option.value} value={option.value}>
                      {option.label}
                    </Select.Option>
                  ))}
                </Select>
              </Col>
              <Col span={3}>
                <Select
                  placeholder="业务类型"
                  style={{ width: '100%' }}
                  value={filters.service || undefined}
                  onChange={(value) => handleFilterChange('service', value || '')}
                  allowClear
                  showSearch
                  data-testid="fup-filter-service"
                >
                  {serviceTypeOptions.map(option => (
                    <Select.Option key={option.value} value={option.value}>
                      {option.label}
                    </Select.Option>
                  ))}
                </Select>
              </Col>
              <Col span={4}>
                <Select
                  placeholder="意向程度"
                  style={{ width: '100%' }}
                  value={filters.intent || undefined}
                  onChange={(value) => handleFilterChange('intent', value || '')}
                  allowClear
                  showSearch
                  data-testid="fup-filter-intent"
                >
                  {(filterOptions?.intents || []).map(option => (
                    <Select.Option key={option.value} value={option.value}>
                      {option.label}
                    </Select.Option>
                  ))}
                </Select>
              </Col>
              <Col span={2}>
                <Select
                  placeholder="状态"
                  style={{ width: '100%' }}
                  value={filters.status || undefined}
                  onChange={(value) => handleFilterChange('status', value || '')}
                  allowClear
                  showSearch
                  data-testid="fup-filter-status"
                >
                  {(filterOptions?.statuses || []).map(option => (
                    <Select.Option key={option.value} value={option.value}>
                      {option.label}
                    </Select.Option>
                  ))}
                </Select>
              </Col>
              <Col span={2.5}>
                <Select
                  placeholder="所属销售"
                  style={{ width: '100%' }}
                  value={filters.user_id ? Number(filters.user_id) : undefined}
                  onChange={(value) => handleFilterChange('user_id', value ? String(value) : '')}
                  allowClear
                  showSearch
                  optionFilterProp="children"
                  data-testid="fup-filter-seller"
                >
                  {(filterOptions?.sellers || []).map(option => (
                    <Select.Option key={option.value} value={option.value}>
                      {option.label}
                    </Select.Option>
                  ))}
                </Select>
              </Col>
            </Row>
          </Col>
        </Row>

        {/* 筛选状态显示 */}
        {(Object.values(filters).some(value => value) || searchText) && (
          <Row style={{ marginBottom: 16 }}>
            <Col span={24}>
              <div style={{
                padding: '8px 12px',
                backgroundColor: '#f0f2f5',
                borderRadius: '6px',
                fontSize: '14px',
                color: '#666'
              }}>
                <Space wrap>
                  <span>已应用筛选条件：</span>
                  {searchText && (
                    <Tag closable onClose={() => setSearchText('')}>
                      搜索: {searchText}
                    </Tag>
                  )}
                  {filters.nation && (
                    <Tag closable onClose={() => handleFilterChange('nation', '')}>
                      国家: {filterOptions?.nations?.find(opt => opt.value === filters.nation)?.label || filters.nation}
                    </Tag>
                  )}
                  {filters.customer_categories && (
                    <Tag closable onClose={() => handleFilterChange('customer_categories', '')}>
                      客户类型: {filterOptions?.customer_categories?.find(opt => opt.value === filters.customer_categories)?.label || filters.customer_categories}
                    </Tag>
                  )}
                  {filters.source && (
                    <Tag closable onClose={() => handleFilterChange('source', '')}>
                      来源: {filterOptions?.sources?.find(opt => opt.value === filters.source)?.label || filters.source}
                    </Tag>
                  )}
                  {filters.service && (
                    <Tag closable onClose={() => handleFilterChange('sub_service', '')}>
                      业务: {filters.service}
                    </Tag>
                  )}
                  {filters.intent && (
                    <Tag closable onClose={() => handleFilterChange('intent', '')}>
                      意向: {filterOptions?.intents?.find(opt => opt.value === filters.intent)?.label || filters.intent}
                    </Tag>
                  )}
                  {filters.status && (
                    <Tag closable onClose={() => handleFilterChange('status', '')}>
                      状态: {filterOptions?.statuses?.find(opt => opt.value === filters.status)?.label || filters.status}
                    </Tag>
                  )}
                  {filters.user_id && (
                    <Tag closable onClose={() => handleFilterChange('user_id', '')}>
                      所属销售: {filterOptions?.sellers?.find(opt => opt.value === Number(filters.user_id))?.label || filters.user_id}
                    </Tag>
                  )}
                  <Button type="link" size="small" onClick={handleResetFilters}>
                    清除所有筛选
                  </Button>
                </Space>
              </div>
            </Col>
          </Row>
        )}

        <Table
          columns={columns}
          dataSource={followUps}
          loading={loading}
          scroll={{ x: 1300 }}
          sticky={{ offsetHeader: 0 }}
          rowSelection={canDelete ? (rowSelection as any) : undefined}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
          }}
          onChange={handleTableChange}
          rowKey="id"
        />
      </Card>

      <Modal
        title={editingFollowUp ? '编辑客户跟进' : '新增客户跟进'}
        open={modalVisible}
        onOk={handleSave}
        onCancel={() => setModalVisible(false)}
        width={800}
        destroyOnHidden
        data-testid="modal-fup"
        okButtonProps={{ 'data-testid': 'btn-fup-save' } as any}
        cancelButtonProps={{ 'data-testid': 'btn-fup-cancel' } as any}
      >
        <Form
          form={form}
          layout="vertical"
        >
          <Row gutter={16}>
              <Col span={8}>
                  <Form.Item
                      label="国家"
                      name="nation"
                      rules={[{ required: true, message: '请选择国家' }]}
                  >
                      <Select
                          placeholder="请选择国家"
                          showSearch
                          filterOption={(input, option) =>
                            String(option?.children || '').toLowerCase().includes(input.toLowerCase())
                          }
                          data-testid="fup-nation"
                      >
                          {nationOptions.map(option => (
                              <Option key={option.value} value={option.value}>
                                  {option.label}
                              </Option>
                          ))}
                      </Select>
                  </Form.Item>
              </Col>
              <Col span={8}>
                  <Form.Item
                      label="业务类型"
                      name="service"
                  >
                      <Select
                          placeholder="请选择业务类型"
                          onChange={handleServiceTypeChange}
                          data-testid="fup-service"
                      >
                          {serviceTypeOptions.map(option => (
                              <Option key={option.value} value={option.value}>
                                  {option.label}
                              </Option>
                          ))}
                      </Select>
                  </Form.Item>
              </Col>
              <Col span={8}>
                  <Form.Item
                      label="子业务名称"
                      name="sub_service"
                  >
                      <Select
                          placeholder="请先选择子业务名称"
                          disabled={!selectedServiceType}
                          data-testid="fup-sub-service"
                      >
                          {selectedServiceType && serviceNameOptions.map(name => (
                            <Option key={name} value={name}>
                              {name}
                            </Option>
                          ))}
                      </Select>
                  </Form.Item>
              </Col>
              <Col span={8}>
                  <Form.Item
                      label="客户类型"
                      name="customer_categories"
                      rules={[{ required: true, message: '请选择客户类型' }]}
                  >
                      <Select placeholder="请选择客户类型" data-testid="fup-customer-categories">
                          {clientTypeOptions.map(option => (
                              <Option key={option.value} value={option.value}>
                                  {option.label}
                              </Option>
                          ))}
                      </Select>
                  </Form.Item>
              </Col>
              <Col span={8}>
                  <Form.Item
                      label="客户来源"
                      name="source"
                  >
                      <Select placeholder="请选择客户来源" data-testid="fup-source">
                          {sourceOptions.map(option => (
                              <Option key={option.value} value={option.value}>
                                  {option.label}
                              </Option>
                          ))}
                      </Select>
                  </Form.Item>
              </Col>
            <Col span={8}>
              <Form.Item
                label="客户名称"
                name="contact_name"
              >
                <Input placeholder="请输入客户名称" data-testid="fup-contact-name" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="微信/电话"
                name="contact_info"
              >
                <Input placeholder="请输入微信或电话" data-testid="fup-contact-info" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="意向程度"
                name="intent"
              >
                <Select placeholder="请选择意向程度" data-testid="fup-intent">
                  {intentOptions.map(option => (
                    <Option key={option.value} value={option.value}>
                      {option.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="跟进状态"
                name="status"
              >
                <Select placeholder="请选择跟进状态" data-testid="fup-status">
                  {statusOptions.map(option => (
                    <Option key={option.value} value={option.value}>
                      {option.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

              <Col span={8}>
                  <Form.Item label="获客日期" name="created_at">
                      <DatePicker style={{ width: '100%' }} placeholder="请选择获客日期" data-testid="fup-created-at" />
                  </Form.Item>
              </Col>
          </Row>

          {/*<Form.Item*/}
          {/*  label="跟进备注"*/}
          {/*  name="remark"*/}
          {/*>*/}
          {/*  <TextArea*/}
          {/*    rows={4}*/}
          {/*    placeholder="请详细记录本次跟进的情况、客户反馈、下次跟进计划等"*/}
          {/*    data-testid="fup-remark"*/}
          {/*  />*/}
          {/*</Form.Item>*/}
        </Form>
      </Modal>

      {/* 查看详情：已改为独立页面 /follow-ups/:id */}

      {/* 导入模态框 */}
      <Modal
        title="导入跟进记录"
        open={importModalVisible}
        onCancel={() => {
          setImportModalVisible(false);
          setImportProgress(0);
        }}
        footer={null}
        width={500}
      >
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <Upload.Dragger
            name="file"
            accept=".xlsx,.xls,.csv"
            beforeUpload={(file) => {
              handleImport(file);
              return false; // 阻止自动上传
            }}
            showUploadList={false}
            disabled={importLoading}
          >
            <p className="ant-upload-drag-icon">
              <UploadOutlined />
            </p>
            <p className="ant-upload-text">点击或拖拽文件到此区域上传</p>
            <p className="ant-upload-hint">
              支持 .xlsx, .xls, .csv 格式文件，单个文件不超过10MB
            </p>
          </Upload.Dragger>

          {importLoading && (
            <div style={{ marginTop: 20 }}>
              <Progress percent={importProgress} />
              <p>导入中，请稍候...</p>
            </div>
          )}

          <div style={{ marginTop: 20, textAlign: 'left' }}>
            <ul style={{ paddingLeft: '20px', fontSize: '12px', color: '#666' }}>
              <li>请使用提供的模板格式进行数据整理</li>
              <li>联系人、联系方式和国家/地区为必填项</li>
              <li>系统会根据"联系人+联系方式"判断记录唯一性</li>
              <li>重复记录将更新现有数据，新记录将被添加</li>
            </ul>
          </div>
        </div>
      </Modal>

      <FollowUpRecordModal
        open={recordModalVisible}
        followUpId={recordFollowUpId}
        onClose={() => {
          setRecordModalVisible(false);
          setRecordFollowUpId(undefined);
          fetchFollowUps();
        }}
      />
    </div>
  );
};

export default FollowUpManagement;
